import { motion } from "framer-motion";
import { money, moneyFull, pct, months, num } from "@/lib/format";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { AlertTriangle } from "lucide-react";
import BenefitBreakdownChart from "@/components/BenefitBreakdownChart";
import SensitivityChart from "@/components/SensitivityChart";

export default function ResultsPanel({ results, currency, modelType, loading, inputs, flags }) {
  if (!results) {
    return (
      <div className="border border-slate-200 bg-white p-8">
        <p className="text-xs tracking-[0.28em] uppercase text-slate-500 mb-3">Live Results</p>
        <p className="text-slate-500 text-sm">Enter your inputs to see the ROI projection.</p>
      </div>
    );
  }
  const { base, conservative, optimistic, top_value_drivers, top_sensitive_assumptions } = results;
  const roiPositive = base.roi_pct >= 0;
  const negativeROI = base.roi_pct < 0;

  return (
    <div className="space-y-4">
      <div className={`border ${roiPositive ? "border-slate-200" : "border-red-200"} bg-white p-8`}>
        <div className="flex items-baseline justify-between mb-4">
          <p className="text-xs tracking-[0.28em] uppercase text-slate-500">Base-case ROI</p>
          {loading && <span className="text-[10px] text-slate-400 mono uppercase tracking-widest">recalculating</span>}
        </div>
        <FormulaHover formula="ROI% = (Total benefit − Total cost) / Total cost × 100">
          <motion.div
            key={base.roi_pct}
            initial={{ opacity: 0.4, y: 2 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.24 }}
            className={`font-heading text-5xl md:text-6xl font-light tracking-tight ${roiPositive ? "text-slate-900" : "text-red-600"}`}
            data-testid="result-roi"
          >
            {pct(base.roi_pct)}
          </motion.div>
        </FormulaHover>

        {negativeROI && (
          <div className="mt-4 flex items-start gap-2 text-red-700 text-xs">
            <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>Costs currently exceed projected benefits. Adjust lift, pickup or cost inputs before approving.</span>
          </div>
        )}

        <div className="grid grid-cols-2 gap-6 mt-8 pt-6 border-t border-slate-200">
          <FormulaHover formula="Net = Total benefit − Total cost">
            <Stat label="Net benefit" value={moneyFull(base.net_benefit, currency)} testid="result-net-benefit" tone={base.net_benefit >= 0 ? "pos" : "neg"} />
          </FormulaHover>
          <FormulaHover formula="Payback = Total cost / Monthly benefit">
            <Stat
              label="Payback"
              value={base.payback_beyond_timeframe && base.payback_months ? `Beyond ${inputs.timeframe_months} mo` : months(base.payback_months)}
              testid="result-payback"
            />
          </FormulaHover>
        </div>

        <div className="grid grid-cols-2 gap-6 mt-6">
          <Stat label="Total benefit" value={moneyFull(base.total_benefit, currency)} testid="result-total-benefit" />
          <Stat label="Total cost" value={moneyFull(base.total_cost, currency)} testid="result-total-cost" />
        </div>
      </div>

      {/* Range card */}
      <div className="border border-slate-200 bg-white p-6">
        <p className="text-xs tracking-[0.28em] uppercase text-slate-500 mb-4">Range (±30% flex)</p>
        <SensitivityChart results={results} currency={currency} height={140} />
        <div className="grid grid-cols-3 gap-3 mt-4">
          <RangeCell label="Conservative" value={pct(conservative.roi_pct)} tone={conservative.roi_pct >= 0 ? "neutral" : "neg"} />
          <RangeCell label="Base" value={pct(base.roi_pct)} tone={roiPositive ? "pos" : "neg"} />
          <RangeCell label="Optimistic" value={pct(optimistic.roi_pct)} tone="pos" />
        </div>
      </div>

      {/* Benefit breakdown */}
      <div className="border border-slate-200 bg-white p-6">
        <p className="text-xs tracking-[0.28em] uppercase text-slate-500 mb-4">Benefit breakdown</p>
        <BenefitBreakdownChart benefits={base.benefits} currency={currency} height={200} />
      </div>

      {/* Drivers */}
      <div className="border border-slate-200 bg-white p-6">
        <p className="text-xs tracking-[0.28em] uppercase text-slate-500 mb-3">Top value drivers</p>
        <ol className="space-y-2">
          {top_value_drivers.map((d, i) => (
            <li key={d.key} className="flex justify-between text-sm">
              <span className="text-slate-700">{i + 1}. {d.label}</span>
              <span className="mono text-slate-900">{money(d.value, currency)}</span>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

function Stat({ label, value, testid, tone = "neutral" }) {
  const color = tone === "neg" ? "text-red-600" : tone === "pos" ? "text-slate-900" : "text-slate-900";
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</div>
      <div className={`font-heading text-xl font-medium mt-1 ${color}`} data-testid={testid}>{value}</div>
    </div>
  );
}

function RangeCell({ label, value, tone }) {
  const color = tone === "neg" ? "text-red-600" : tone === "pos" ? "text-emerald-600" : "text-slate-900";
  return (
    <div className="border border-slate-100 p-3">
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</div>
      <div className={`font-heading text-lg mt-1 font-medium ${color}`}>{value}</div>
    </div>
  );
}

function FormulaHover({ formula, children }) {
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="inline-block hover:bg-slate-50 rounded-sm transition-colors">{children}</div>
        </TooltipTrigger>
        <TooltipContent className="bg-slate-800 text-white text-xs border-0 max-w-xs">
          <div className="mono">{formula}</div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
